import { Text, Heading } from "@otrium/atoms";
import { Box } from "@otrium/core";
import { Trans } from "@lingui/macro";
import { Overlay } from "src/atoms/Overlay";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  StyledButton,
  StyledCloseButton,
  Wrapper,
} from "./SubscribeOverlay.styled";
import { Content } from "src/atoms/Content";
import SubscribeModal from "./SubscribeModal";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useSession } from "next-auth/client";
import Cookies from "js-cookie";
import { AppCtx } from "src/contexts/app.context";
import { AuthProvider } from "pages/api/auth/getUserTokenFromApi";
import { CONSENT_NOT_REQUIRED_LOCALES } from "src/molecules/ConsentMangerBanner/utils";
import { useSegment } from "src/hooks/useSegment";
import getConfig from "next/config";
import { Locale } from "lib/Intl";

const OPEN_TIME = 2000;
const COOKIE_NAME = "otrium_cookie-overlay_opt_in";
export const OPTED_IN_COOKIE_NAME = "otrium_cookie-guest-opted-in";
const { publicRuntimeConfig } = getConfig();
const FAQ_LINK_BY_LOCALE: Record<Locale, string> = {
  en: "https://help.otrium.nl/en/articles/92-what-are-the-conditions-of-our-discount-codes",
  de: "https://help.otrium.nl/de/articles/92-was-sind-die-bedingungen-fuer-unsere-rabattcodes",
  nl: "https://help.otrium.nl/nl/articles/92-wat-zijn-de-voorwaarden-van-onze-kortingscodes",
  fr: "https://help.otrium.nl/fr/articles/92-quelles-sont-les-conditions-d-utilisation-de-nos-codes-de-reduction",
  "en-gb": "https://help.otrium.nl/en-gb/articles/561",
  "de-at": "https://help.otrium.nl/en/articles/561",
  "nl-be":
    "https://help.otrium.nl/nl/articles/92-wat-zijn-de-voorwaarden-van-onze-kortingscodes",
  "en-dk": "https://help.otrium.nl/en/articles/561",
  "en-se": "https://help.otrium.nl/en/articles/561",
  "pl-pl":
    "https://help.otrium.nl/pl/articles/92-jakie-s-warunki-naszych-kodow-rabatowych",
  "es-es":
    "https://help.otrium.nl/es/articles/92-cuales-son-las-condiciones-de-nuestros-codigos-de-descuento",
  "it-it": "https://help.otrium.nl/it/articles/561",
  "en-us": "https://help.otrium.nl/en/articles/561",
};

const SubscribeOverlay = (): JSX.Element => {
  const bannerViewed = useRef(false);
  const { displayConsentManager, locale } = useContext(AppCtx);
  const isLoggedIn = useIsLoggedIn();
  const [session] = useSession();
  const isUserOptIn = session?.user.opt_in;
  const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isScrolledDown, setIsScrolledDown] = useState<boolean>(false);
  const cookiesOverlayOptIn = Cookies.get(COOKIE_NAME);
  const timerId = useRef<number>();
  const [submitted, setSubmitted] = useState(false);
  const storeRequiresConsent = !CONSENT_NOT_REQUIRED_LOCALES.includes(locale);
  const isConsentManagerShown = displayConsentManager && storeRequiresConsent;

  const {
    segmentGuestOptInBannerViewed,
    segmentGuestOptInBannerDismissed,
    segmentGuestOptInBannerCtaClick,
    segmentGuestOptInPopupDismissed,
  } = useSegment();

  useEffect(() => {
    if (cookiesOverlayOptIn === undefined) {
      Cookies.set(COOKIE_NAME, "1");
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldShowOverlay = useMemo(() => {
    return (
      !isLoggedIn &&
      !(
        session?.user?.signInPlatform === AuthProvider.CREDENTIALS_SOFT_LOGIN
      ) &&
      !isModalOpen &&
      !isUserOptIn &&
      !isOverlayOpen &&
      cookiesOverlayOptIn === "1" &&
      !isConsentManagerShown &&
      isScrolledDown
    );
  }, [
    cookiesOverlayOptIn,
    isLoggedIn,
    isModalOpen,
    isOverlayOpen,
    isUserOptIn,
    isConsentManagerShown,
    session?.user.signInPlatform,
    isScrolledDown,
  ]);

  useEffect(() => {
    if (shouldShowOverlay && !bannerViewed.current) {
      bannerViewed.current = true;
      void segmentGuestOptInBannerViewed();
    }
  }, [shouldShowOverlay]);

  const handleClose = useCallback((closeModal = false) => {
    setIsOverlayOpen(false);
    Cookies.set(COOKIE_NAME, "0");
    if (closeModal) {
      setIsModalOpen(false);
    }
  }, []);

  const handleScroll = () => {
    const documentHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    const scrollPosition = window.scrollY;
    const scrollPercentage =
      (scrollPosition / (documentHeight - viewportHeight)) * 100;
    setIsScrolledDown(false);

    if (scrollPercentage >= 5) {
      setIsScrolledDown(true);
    }
  };

  const handleFormSubmit = useCallback(() => {
    Cookies.set(COOKIE_NAME, "0");
    Cookies.set(OPTED_IN_COOKIE_NAME, "1");
    setSubmitted(true);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    clearTimeout(timerId.current);

    if (shouldShowOverlay) {
      timerId.current = window.setTimeout(() => {
        setIsOverlayOpen(true);
        setIsScrolledDown(false);
      }, OPEN_TIME);
    }
  }, [shouldShowOverlay]);

  return (
    <>
      <Overlay isOpen={isOverlayOpen} />
      <Wrapper open={isOverlayOpen}>
        <Content sx={{ position: "relative" }}>
          <StyledCloseButton
            onClick={() => {
              handleClose();
              void segmentGuestOptInBannerDismissed();
            }}
            iconSize={16}
          />
          <Box>
            <Heading
              as="h3"
              variant="h3"
              mb={2}
              fontSize="24px"
              fontFamily="PlusJakartaSans"
              letterSpacing="normal"
              lineHeight="32px"
              pb={0}
              maxWidth={`calc(100% - 16px)`}
              sx={{
                textTransform: "none",
                textAlign: ["left", "left", "center"],
                paddingLeft: [0, 0, 3],
              }}
            >
              <Trans>Get 15% off your first order</Trans>
            </Heading>
            <Text
              variant="body.source.regular16"
              as="p"
              mb={3}
              sx={{
                textAlign: ["left", "left", "center"],
              }}
            >
              <Trans>
                Over 400 brands, new daily fashion deals, and prices you’ll
                love!
              </Trans>
            </Text>
            <StyledButton
              variant="primary"
              onClick={() => {
                setIsModalOpen(true);
                setIsOverlayOpen(false);
                void segmentGuestOptInBannerCtaClick();
              }}
            >
              <Trans>Subscribe now</Trans>
            </StyledButton>
            <Text
              variant="body.source.regular16"
              as="p"
              mt={3}
              fontSize={0}
              fontStyle="italic"
              textDecoration="underline"
              sx={{
                textAlign: ["left", "left", "center"],
              }}
            >
              <a
                href={`${
                  FAQ_LINK_BY_LOCALE[publicRuntimeConfig.LOCALE as Locale] ||
                  "https://help.otrium.nl/en/articles/561"
                }`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Trans>subscribe-sub-text</Trans>
              </a>
            </Text>
          </Box>
        </Content>
      </Wrapper>
      <SubscribeModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          void segmentGuestOptInPopupDismissed();
        }}
        onFormSubmit={handleFormSubmit}
        submitted={submitted}
      />
    </>
  );
};

export default SubscribeOverlay;
